import { SxProps, Theme } from "@mui/material";
import inboxTokens, {
  inboxResponsiveTokens,
  inboxStatusTokens,
  InboxStatusKey,
  PHONE_SM_QUERY,
} from "./Inbox.tokens";

const {
  page,
  header,
  primaryButton,
  toolbar,
  tabs,
  list,
  row,
  status,
  rowAction,
  footer,
  freshState,
  noResults,
} = inboxTokens;

const { phone, tablet } = inboxResponsiveTokens;

/** Shared chrome for the search field and the date-range trigger beside it. */
const toolbarField = {
  display: "flex",
  alignItems: "center",
  bgcolor: toolbar.bg,
  border: `1px solid ${toolbar.border}`,
  borderRadius: toolbar.radius,
  p: {
    xs: phone.search.padding,
    sm: tablet.toolbar.padding,
    md: toolbar.padding,
  },
  boxSizing: "border-box",
} as const;

/** Both empty-state cards share the list card's fill and hairline. */
const stateCard = {
  bgcolor: list.bg,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
} as const;

const inboxStyles = {
  /**
   * Below `md` the reference lays the page out edge to edge with its own
   * gutters (14/16px on the phone, 26px on the tablet), so the shared
   * `CenteredAuthenticatedLayout` gutter and top margin are pulled back off
   * with negative margins first and the reference values applied on top. It
   * also spaces the blocks with one column gap instead of the per-block bottom
   * margins the desktop screen uses. From `md` up nothing changes.
   */
  page: {
    display: "flex",
    flexDirection: "column",
    gap: { xs: phone.gap.std, sm: tablet.gap, md: 0 },
    mt: { xs: "-16px", sm: "-32px", md: 0 },
    mx: { xs: "-16px", sm: "-24px", md: 0 },
    pt: {
      xs: phone.paddingTop.std,
      sm: tablet.paddingTop,
      md: page.paddingTop,
    },
    px: { xs: phone.paddingX.std, sm: tablet.paddingX, md: page.paddingX },
    pb: {
      xs: phone.paddingBottom.std,
      sm: tablet.paddingBottom,
      md: page.paddingBottom,
    },
    [PHONE_SM_QUERY]: {
      gap: phone.gap.sm,
      paddingTop: phone.paddingTop.sm,
      paddingLeft: phone.paddingX.sm,
      paddingRight: phone.paddingX.sm,
      paddingBottom: phone.paddingBottom.sm,
    },
  } as SxProps<Theme>,

  header: {
    display: "flex",
    // The tablet reference bottom-aligns the button with the strapline; the
    // phone has no title above the strapline left to align against.
    alignItems: { xs: "center", sm: "flex-end", md: "center" },
    justifyContent: "space-between",
    flexDirection: "row",
    gap: { xs: "12px", sm: tablet.headerGap, md: header.gap },
    mb: { xs: 0, sm: 0, md: header.marginBottom },
  } as SxProps<Theme>,

  /** Title + strapline column; must be shrinkable beside the phone button. */
  headerText: {
    minWidth: 0,
  } as SxProps<Theme>,

  /** The phone reference carries the screen name in the app bar, not here. */
  headerTitle: {
    display: { xs: "none", sm: "block" },
    m: 0,
    color: header.titleColor,
    fontSize: { sm: tablet.titleFontSize, md: header.titleFontSize },
    fontWeight: header.titleFontWeight,
    letterSpacing: header.titleLetterSpacing,
    lineHeight: "normal",
  } as SxProps<Theme>,

  headerSubtitle: {
    mt: { xs: 0, sm: header.subtitleMarginTop },
    color: header.subtitleColor,
    fontSize: {
      xs: phone.subtitleFontSize.std,
      sm: tablet.subtitleFontSize,
      md: header.subtitleFontSize,
    },
    lineHeight: { xs: phone.subtitleLineHeight, sm: "normal" },
    [PHONE_SM_QUERY]: { fontSize: phone.subtitleFontSize.sm },
  } as SxProps<Theme>,

  /** Holds the split button so it never shrinks against the title block. */
  headerActions: {
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    flexShrink: 0,
  } as SxProps<Theme>,

  /**
   * Phone "Create Request" — full width under the header.
   *
   * Was a 40px square beside the strapline, and nothing at all below 390,
   * which left the narrow column with no way to start a request. The
   * full-width button is what the other list screens use.
   */
  createMobile: {
    display: { xs: "inline-flex", sm: "none" },
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: phone.createButton.gap,
    p: phone.createButton.padding,
    borderRadius: phone.createButton.radius,
    bgcolor: primaryButton.bg,
    color: primaryButton.color,
    fontSize: phone.createButton.fontSize,
    fontWeight: primaryButton.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: primaryButton.hoverBg },
    "&.Mui-disabled": {
      bgcolor: primaryButton.bg,
      color: primaryButton.color,
      opacity: 0.6,
    },
  } as SxProps<Theme>,

  /** Main half of the "Create Request" split button. */
  /*
   * The "Create Request" split button carries no screen styling: its fill,
   * caret half, divider, radius and type all come from `palette.splitButton`
   * via CustomDropdownButton, so this header matches the side nav and every
   * other instance. Only `primaryButton.iconSize` is still read, by the glyph.
   */

  toolbar: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    gap: { xs: phone.search.gap, sm: tablet.toolbar.gap, md: toolbar.gap },
    mb: { xs: 0, sm: 0, md: toolbar.marginBottom },
  } as SxProps<Theme>,

  searchField: {
    ...toolbarField,
    flex: 1,
    width: "100%",
    minWidth: 0,
    gap: {
      xs: phone.search.gap,
      sm: tablet.toolbar.searchGap,
      md: toolbar.searchGap,
    },
    "&:focus-within": { borderColor: toolbar.focusBorder },
  } as SxProps<Theme>,

  searchInput: {
    flex: 1,
    minWidth: 0,
    "& .MuiInputBase-input": {
      p: toolbar.inputPadding,
      height: "auto",
      fontSize: {
        xs: phone.search.inputFontSize,
        sm: tablet.toolbar.inputFontSize,
        md: toolbar.inputFontSize,
      },
      fontWeight: toolbar.inputFontWeight,
      lineHeight: "normal",
      color: toolbar.inputColor,
      "&::placeholder": {
        color: toolbar.placeholderColor,
        opacity: 1,
        fontWeight: toolbar.inputFontWeight,
      },
    },
  } as SxProps<Theme>,

  /** The reference drops the date-range chip below `sm` — search only. */
  dateField: {
    ...toolbarField,
    display: { xs: "none", sm: "flex" },
    gap: { sm: tablet.toolbar.dateGap, md: toolbar.dateGap },
    flexShrink: 0,
    fontSize: { sm: tablet.toolbar.dateFontSize, md: toolbar.dateFontSize },
    fontWeight: toolbar.dateFontWeight,
    color: toolbar.dateColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    cursor: "pointer",
    "&:hover": { borderColor: toolbar.hoverBorder },
  } as SxProps<Theme>,

  /**
   * Re-skins `CustomDateRangePicker`'s trigger into the design's compact
   * "All time ▾" chip — the shared control defaults to a 380px field, and an
   * `<input>` at `width: auto` still reserves ~20 characters. `fieldSizing`
   * collapses it onto its own text (measured pixel-exact against the design);
   * the picker's `autoSizeInput` prop carries browsers that lack it.
   */
  datePickerTrigger: {
    minWidth: "0 !important",
    "& .MuiOutlinedInput-root": { p: 0 },
    "& .MuiInputBase-input, & .MuiOutlinedInput-input": {
      width: "auto",
      fieldSizing: "content",
      p: 0,
      fontSize: { sm: tablet.toolbar.dateFontSize, md: toolbar.dateFontSize },
      fontWeight: toolbar.dateFontWeight,
      color: toolbar.dateColor,
      lineHeight: "normal",
      textOverflow: "ellipsis",
    },
  } as SxProps<Theme>,

  /**
   * The phone reference runs the pills as one horizontally-scrolling line that
   * bleeds into the page gutters, so the first chip still lines up with the
   * cards while the rest scroll past the edge. The tablet keeps the desktop's
   * wrapping row.
   */
  /**
   * Wrapper `ScrollHintRail` positions its chevrons against; owns the spacing
   * the strip itself used to carry.
   */
  tabsRail: {
    mb: { xs: 0, sm: 0, md: tabs.marginBottom },
  } as SxProps<Theme>,

  /*
    No negative margins. The strip used to bleed into the page gutter, so it
    sat wider than every other block and its last pill ran under the viewport
    edge with nothing saying it scrolls.
  */
  tabs: {
    flexWrap: { xs: "nowrap", sm: "wrap" },
    overflowX: { xs: "auto", sm: "visible" },
    gap: { xs: phone.tabs.gap, sm: tabs.gap },
  } as SxProps<Theme>,

  tab: (active: boolean) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      gap: { xs: phone.tabs.labelGap, sm: tabs.labelGap },
      minWidth: 0,
      // Nothing may shrink inside the phone's scrolling strip.
      flexShrink: 0,
      height: "auto",
      p: { xs: phone.tabs.padding.std, sm: tabs.padding },
      borderRadius: tabs.radius,
      border: `1px solid ${active ? tabs.activeBorder : tabs.border}`,
      bgcolor: active ? tabs.activeBg : tabs.bg,
      color: active ? tabs.activeColor : tabs.color,
      fontSize: { xs: phone.tabs.fontSize, sm: tabs.fontSize },
      fontWeight: tabs.fontWeight,
      lineHeight: "normal",
      textTransform: "none",
      whiteSpace: "nowrap",
      boxShadow: "none",
      "&:hover": {
        bgcolor: active ? tabs.activeBg : tabs.bg,
        borderColor: active ? tabs.activeBorder : tabs.border,
        boxShadow: "none",
      },
      "&.Mui-disabled": {
        bgcolor: active ? tabs.activeBg : tabs.bg,
        color: active ? tabs.activeColor : tabs.color,
        opacity: 0.6,
      },
      [PHONE_SM_QUERY]: { padding: phone.tabs.padding.sm },
    }) as SxProps<Theme>,

  tabCount: (active: boolean) =>
    ({
      minWidth: { xs: phone.tabs.countMinWidth, sm: tabs.countMinWidth },
      p: tabs.countPadding,
      borderRadius: tabs.countRadius,
      bgcolor: active ? tabs.countActiveBg : tabs.countBg,
      color: active ? tabs.countActiveColor : tabs.countColor,
      fontSize: { xs: phone.tabs.countFontSize, sm: tabs.countFontSize },
      fontWeight: tabs.countFontWeight,
      lineHeight: "normal",
      textAlign: "center",
    }) as SxProps<Theme>,

  /**
   * On a phone the reference gives every request its own card, so the wrapper
   * loses its chrome and becomes a plain stack; from `sm` up it is the single
   * white card the desktop screen draws.
   */
  listCard: {
    display: { xs: "flex", sm: "block" },
    flexDirection: "column",
    gap: { xs: phone.list.gap, sm: 0 },
    bgcolor: { xs: "transparent", sm: list.bg },
    border: { xs: "none", sm: `1px solid ${list.border}` },
    borderRadius: { xs: 0, sm: list.radius },
    boxShadow: { xs: "none", sm: list.shadow },
    overflow: { xs: "visible", sm: "hidden" },
  } as SxProps<Theme>,

  row: {
    position: "relative",
    display: "flex",
    // Phone: document block over the status / action line. Tablet and desktop:
    // one line, as before.
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    gap: { xs: phone.row.gap.std, sm: tablet.row.gap, md: row.gap },
    p: { xs: phone.row.padding.std, sm: tablet.row.padding, md: row.padding },
    bgcolor: { xs: list.bg, sm: "transparent" },
    border: { xs: `1px solid ${list.border}`, sm: "none" },
    borderRadius: { xs: phone.row.radius, sm: 0 },
    // The design keeps the divider on the last row too, so it reads as a
    // hairline just inside the card's bottom edge.
    borderBottom: {
      xs: `1px solid ${list.border}`,
      sm: `1px solid ${row.divider}`,
    },
    "&:hover": { bgcolor: { xs: list.bg, sm: row.hoverBg } },
    [PHONE_SM_QUERY]: {
      gap: phone.row.gap.sm,
      padding: phone.row.padding.sm,
    },
  } as SxProps<Theme>,

  rowAccent: (key: InboxStatusKey) =>
    ({
      position: "absolute",
      left: 0,
      // Inset from the card's rounded corners on a phone; full-bleed inside the
      // single card from `sm` up.
      top: { xs: phone.row.accentInset.std, sm: 0 },
      bottom: { xs: phone.row.accentInset.std, sm: 0 },
      width: row.accentWidth,
      borderRadius: { xs: phone.row.accentRadius, sm: 0 },
      bgcolor: inboxStatusTokens[key].accent,
      [PHONE_SM_QUERY]: {
        top: phone.row.accentInset.sm,
        bottom: phone.row.accentInset.sm,
      },
    }) as SxProps<Theme>,

  /**
   * Tile + title, the phone card's first line. `display: contents` from `sm`
   * up hands both straight back to the row so the one-line layout is unchanged
   * — no second copy of the markup.
   */
  rowMain: {
    display: { xs: "flex", sm: "contents" },
    alignItems: "flex-start",
    gap: phone.row.headGap.std,
    minWidth: 0,
    [PHONE_SM_QUERY]: { gap: phone.row.headGap.sm },
  } as SxProps<Theme>,

  /** Status chip + action, the phone card's second line. */
  rowMeta: {
    display: { xs: "flex", sm: "contents" },
    alignItems: "center",
    justifyContent: "space-between",
    gap: phone.row.metaGap,
  } as SxProps<Theme>,

  iconTile: (key: InboxStatusKey) =>
    ({
      width: {
        xs: phone.row.iconTileSize.std,
        sm: tablet.row.iconTileSize,
        md: row.iconTileSize,
      },
      height: {
        xs: phone.row.iconTileSize.std,
        sm: tablet.row.iconTileSize,
        md: row.iconTileSize,
      },
      borderRadius: {
        xs: phone.row.iconTileRadius,
        sm: tablet.row.iconTileRadius,
        md: row.iconTileRadius,
      },
      bgcolor: inboxStatusTokens[key].iconBg,
      color: inboxStatusTokens[key].iconStroke,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      [PHONE_SM_QUERY]: {
        width: phone.row.iconTileSize.sm,
        height: phone.row.iconTileSize.sm,
      },
    }) as SxProps<Theme>,

  /** Glyph inside the tile, sized per breakpoint. */
  rowIcon: {
    width: {
      xs: phone.row.iconSize.std,
      sm: tablet.row.iconSize,
      md: row.iconSize,
    },
    height: {
      xs: phone.row.iconSize.std,
      sm: tablet.row.iconSize,
      md: row.iconSize,
    },
    [PHONE_SM_QUERY]: {
      width: phone.row.iconSize.sm,
      height: phone.row.iconSize.sm,
    },
  } as SxProps<Theme>,

  rowText: {
    flex: 1,
    minWidth: 0,
  } as SxProps<Theme>,

  rowTitle: (muted: boolean) =>
    ({
      fontSize: {
        xs: phone.row.titleFontSize.std,
        sm: tablet.row.titleFontSize,
        md: row.titleFontSize,
      },
      fontWeight: row.titleFontWeight,
      lineHeight: "normal",
      color: muted ? row.titleMutedColor : row.titleColor,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [PHONE_SM_QUERY]: { fontSize: phone.row.titleFontSize.sm },
    }) as SxProps<Theme>,

  rowSub: {
    mt: row.subMarginTop,
    fontSize: {
      xs: phone.row.subFontSize.std,
      sm: tablet.row.subFontSize,
      md: row.subFontSize,
    },
    color: row.subColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [PHONE_SM_QUERY]: { fontSize: phone.row.subFontSize.sm },
  } as SxProps<Theme>,

  statusChip: (key: InboxStatusKey) =>
    ({
      display: "inline-flex",
      alignItems: "center",
      gap: { xs: phone.status.chipGap, sm: status.chipGap },
      p: { xs: phone.status.chipPadding.std, sm: status.chipPadding },
      borderRadius: status.chipRadius,
      bgcolor: inboxStatusTokens[key].bg,
      flexShrink: 0,
      [PHONE_SM_QUERY]: { padding: phone.status.chipPadding.sm },
    }) as SxProps<Theme>,

  statusDot: (key: InboxStatusKey) =>
    ({
      width: { xs: phone.status.dotSize, sm: status.dotSize },
      height: { xs: phone.status.dotSize, sm: status.dotSize },
      borderRadius: "50%",
      bgcolor: inboxStatusTokens[key].dot,
      flexShrink: 0,
    }) as SxProps<Theme>,

  statusLabel: (key: InboxStatusKey) =>
    ({
      fontSize: {
        xs: phone.status.labelFontSize.std,
        sm: status.labelFontSize,
      },
      fontWeight: status.labelFontWeight,
      lineHeight: "normal",
      color: inboxStatusTokens[key].color,
      whiteSpace: "nowrap",
      [PHONE_SM_QUERY]: { fontSize: phone.status.labelFontSize.sm },
    }) as SxProps<Theme>,

  /**
   * A fixed slot keeps every status chip on the same vertical line in the
   * one-line layout. The phone card has nothing to line up with, so the slot
   * collapses onto the button.
   */
  actionSlot: {
    width: {
      xs: "auto",
      sm: tablet.row.actionSlotWidth,
      md: row.actionSlotWidth,
    },
    display: "flex",
    justifyContent: "flex-end",
    flexShrink: 0,
  } as SxProps<Theme>,

  rowActionPrimary: {
    minWidth: 0,
    height: "auto",
    p: {
      xs: phone.rowAction.primaryPadding,
      sm: tablet.rowAction.primaryPadding,
      md: rowAction.primaryPadding,
    },
    background: rowAction.primaryBg,
    borderRadius: rowAction.radius,
    color: rowAction.primaryColor,
    fontSize: {
      xs: phone.rowAction.fontSize,
      sm: tablet.rowAction.fontSize,
      md: rowAction.fontSize,
    },
    fontWeight: rowAction.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    boxShadow: "none",
    "&:hover": { background: rowAction.primaryHoverBg, boxShadow: "none" },
  } as SxProps<Theme>,

  rowActionSecondary: {
    minWidth: 0,
    height: "auto",
    p: {
      xs: phone.rowAction.secondaryPadding,
      sm: tablet.rowAction.secondaryPadding,
      md: rowAction.secondaryPadding,
    },
    bgcolor: rowAction.secondaryBg,
    border: `1px solid ${rowAction.secondaryBorder}`,
    borderRadius: rowAction.radius,
    color: rowAction.secondaryColor,
    fontSize: {
      xs: phone.rowAction.fontSize,
      sm: tablet.rowAction.fontSize,
      md: rowAction.fontSize,
    },
    fontWeight: rowAction.fontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      bgcolor: rowAction.secondaryBg,
      borderColor: rowAction.secondaryHoverBorder,
      color: rowAction.secondaryHoverColor,
    },
  } as SxProps<Theme>,

  /**
   * Full label for the desktop's wider action slot, compact one for the phone
   * and tablet. Both spellings are in the DOM and the breakpoint picks one, so
   * no viewport listener is needed.
   */
  rowActionLabelFull: {
    display: { xs: "none", md: "inline" },
  } as SxProps<Theme>,

  rowActionLabelCompact: {
    display: { xs: "inline", md: "none" },
  } as SxProps<Theme>,

  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    // The phone reference keeps the count and the pager on one line.
    flexWrap: { xs: "nowrap", sm: "wrap" },
    gap: footer.gap,
    pt: { xs: phone.footer.paddingTop, sm: 0 },
    mt: { xs: 0, sm: 0, md: footer.marginTop },
  } as SxProps<Theme>,

  footerLabel: {
    fontSize: {
      xs: phone.footer.labelFontSize.std,
      sm: footer.labelFontSize,
    },
    color: footer.labelColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
    [PHONE_SM_QUERY]: { fontSize: phone.footer.labelFontSize.sm },
  } as SxProps<Theme>,

  /** Hidden below `sm`, where "Showing" is dropped too. */
  footerLabelPrefix: {
    display: { xs: "none", sm: "inline" },
  } as SxProps<Theme>,

  pager: {
    display: "flex",
    alignItems: "center",
    gap: footer.gap,
  } as SxProps<Theme>,

  pagerPage: (active: boolean) =>
    ({
      minWidth: footer.pageMinWidth,
      width: "auto",
      height: footer.pageHeight,
      p: footer.pagePadding,
      borderRadius: footer.pageRadius,
      border: `1px solid ${active ? footer.pageActiveBorder : footer.pageBorder}`,
      bgcolor: active ? footer.pageActiveBg : footer.pageBg,
      color: active ? footer.pageActiveColor : footer.pageColor,
      fontSize: footer.pageFontSize,
      fontWeight: footer.pageFontWeight,
      lineHeight: "normal",
      textTransform: "none",
      boxShadow: "none",
      "&:hover": {
        bgcolor: active ? footer.pageActiveBg : footer.pageBg,
        borderColor: active ? footer.pageActiveBorder : footer.pageBorder,
        boxShadow: "none",
      },
    }) as SxProps<Theme>,

  /** Non-interactive gap marker; keeps a page slot's footprint. */
  pagerEllipsis: {
    minWidth: footer.pageMinWidth,
    height: footer.pageHeight,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: footer.ellipsisColor,
    fontSize: footer.pageFontSize,
    fontWeight: footer.pageFontWeight,
    // The ellipsis glyph sits on the baseline, which centring the *line box*
    // leaves 3px low against the page buttons. Collapsing the line box puts the
    // baseline on the centre line, where the dots read as centred.
    lineHeight: 0,
    userSelect: "none",
  } as SxProps<Theme>,

  /** No room for it beside the pager on a phone. */
  jumpGroup: {
    display: { xs: "none", sm: "flex" },
    alignItems: "center",
    gap: footer.gap,
    mr: footer.jumpGap,
  } as SxProps<Theme>,

  jumpLabel: {
    fontSize: footer.jumpLabelFontSize,
    color: footer.jumpLabelColor,
    lineHeight: "normal",
    whiteSpace: "nowrap",
  } as SxProps<Theme>,

  jumpInput: {
    // `InputBase` is inline-flex and sizes to the input's own intrinsic width
    // (~20 characters). Without a flex wrapper it hangs off the top of the
    // chip and overflows it, so the wrapper both centres and constrains it.
    display: "inline-flex",
    alignItems: "center",
    width: footer.jumpInputWidth,
    height: footer.pageHeight,
    px: footer.jumpInputPaddingX,
    bgcolor: footer.jumpInputBg,
    border: `1px solid ${footer.jumpInputBorder}`,
    borderRadius: footer.pageRadius,
    "&:focus-within": { borderColor: footer.jumpInputFocusBorder },
    "& .MuiInputBase-root": { width: "100%" },
    "& .MuiInputBase-input": {
      p: 0,
      height: "auto",
      textAlign: "center",
      fontSize: footer.jumpInputFontSize,
      fontWeight: footer.jumpInputFontWeight,
      lineHeight: "normal",
      color: footer.jumpInputColor,
      "&::placeholder": {
        color: footer.jumpInputPlaceholderColor,
        opacity: 1,
        fontWeight: footer.jumpInputFontWeight,
      },
    },
  } as SxProps<Theme>,

  pagerArrow: {
    minWidth: footer.arrowSize,
    width: footer.arrowSize,
    height: footer.arrowSize,
    p: 0,
    borderRadius: footer.pageRadius,
    border: `1px solid ${footer.pageBorder}`,
    bgcolor: footer.pageBg,
    color: footer.pageColor,
    boxShadow: "none",
    "&:hover": {
      bgcolor: footer.pageBg,
      borderColor: footer.pageBorder,
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      bgcolor: footer.pageBg,
      border: `1px solid ${footer.pageBorder}`,
      color: footer.arrowDisabledColor,
    },
  } as SxProps<Theme>,

  freshIcon: {
    width: { xs: phone.freshState.iconSize.std, sm: freshState.iconSize },
    height: { xs: phone.freshState.iconSize.std, sm: freshState.iconSize },
    borderRadius: "50%",
    bgcolor: freshState.iconBg,
    color: freshState.iconStroke,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: {
      xs: phone.freshState.iconMarginBottom.std,
      sm: freshState.iconMarginBottom,
    },
    [PHONE_SM_QUERY]: {
      width: phone.freshState.iconSize.sm,
      height: phone.freshState.iconSize.sm,
      marginBottom: phone.freshState.iconMarginBottom.sm,
    },
  } as SxProps<Theme>,

  freshGlyph: {
    width: { xs: phone.freshState.glyphSize.std, sm: freshState.glyphSize },
    height: { xs: phone.freshState.glyphSize.std, sm: freshState.glyphSize },
    [PHONE_SM_QUERY]: {
      width: phone.freshState.glyphSize.sm,
      height: phone.freshState.glyphSize.sm,
    },
  } as SxProps<Theme>,

  freshTitle: {
    fontSize: {
      xs: phone.freshState.titleFontSize.std,
      sm: freshState.titleFontSize,
    },
    fontWeight: freshState.titleFontWeight,
    color: freshState.titleColor,
    lineHeight: "normal",
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.titleFontSize.sm },
  } as SxProps<Theme>,

  freshBody: {
    mt: freshState.bodyMarginTop,
    maxWidth: { xs: "none", sm: tablet.freshState.bodyMaxWidth },
    fontSize: {
      xs: phone.freshState.bodyFontSize.std,
      sm: freshState.bodyFontSize,
    },
    color: freshState.bodyColor,
    lineHeight: {
      xs: phone.freshState.bodyLineHeight,
      sm: freshState.bodyLineHeight,
    },
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.bodyFontSize.sm },
  } as SxProps<Theme>,

  freshHint: {
    mt: { xs: phone.freshState.hintMarginTop, sm: freshState.hintMarginTop },
    fontSize: {
      xs: phone.freshState.hintFontSize.std,
      sm: freshState.hintFontSize,
    },
    color: freshState.hintColor,
    lineHeight: "normal",
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.freshState.hintFontSize.sm },
  } as SxProps<Theme>,

  freshHintStrong: {
    fontWeight: freshState.hintStrongWeight,
    color: freshState.hintStrongColor,
  } as SxProps<Theme>,

  noResultsCard: {
    ...stateCard,
    border: `1px solid ${noResults.border}`,
    borderRadius: { xs: phone.noResults.radius, md: noResults.radius },
    boxShadow: noResults.shadow,
    p: {
      xs: phone.noResults.padding.std,
      sm: tablet.noResults.padding,
      md: noResults.padding,
    },
    [PHONE_SM_QUERY]: { padding: phone.noResults.padding.sm },
  } as SxProps<Theme>,

  noResultsIcon: {
    width: {
      xs: phone.noResults.iconSize.std,
      sm: tablet.noResults.iconSize,
      md: noResults.iconSize,
    },
    height: {
      xs: phone.noResults.iconSize.std,
      sm: tablet.noResults.iconSize,
      md: noResults.iconSize,
    },
    borderRadius: "50%",
    bgcolor: noResults.iconBg,
    color: noResults.iconStroke,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: {
      xs: phone.noResults.iconMarginBottom.std,
      sm: tablet.noResults.iconMarginBottom,
      md: noResults.iconMarginBottom,
    },
    [PHONE_SM_QUERY]: {
      width: phone.noResults.iconSize.sm,
      height: phone.noResults.iconSize.sm,
      marginBottom: phone.noResults.iconMarginBottom.sm,
    },
  } as SxProps<Theme>,

  noResultsGlyph: {
    width: {
      xs: phone.noResults.glyphSize.std,
      sm: tablet.noResults.glyphSize,
      md: noResults.glyphSize,
    },
    height: {
      xs: phone.noResults.glyphSize.std,
      sm: tablet.noResults.glyphSize,
      md: noResults.glyphSize,
    },
    [PHONE_SM_QUERY]: {
      width: phone.noResults.glyphSize.sm,
      height: phone.noResults.glyphSize.sm,
    },
  } as SxProps<Theme>,

  noResultsTitle: {
    fontSize: {
      xs: phone.noResults.titleFontSize.std,
      sm: tablet.noResults.titleFontSize,
      md: noResults.titleFontSize,
    },
    fontWeight: noResults.titleFontWeight,
    color: noResults.titleColor,
    lineHeight: "normal",
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.noResults.titleFontSize.sm },
  } as SxProps<Theme>,

  noResultsBody: {
    mt: {
      xs: phone.noResults.bodyMarginTop,
      sm: tablet.noResults.bodyMarginTop,
      md: noResults.bodyMarginTop,
    },
    maxWidth: { xs: "none", sm: noResults.bodyMaxWidth },
    fontSize: {
      xs: phone.noResults.bodyFontSize.std,
      sm: tablet.noResults.bodyFontSize,
      md: noResults.bodyFontSize,
    },
    color: noResults.bodyColor,
    lineHeight: {
      xs: phone.noResults.bodyLineHeight,
      sm: noResults.bodyLineHeight,
    },
    textAlign: "center",
    [PHONE_SM_QUERY]: { fontSize: phone.noResults.bodyFontSize.sm },
  } as SxProps<Theme>,

  noResultsButton: {
    mt: {
      xs: phone.noResults.buttonMarginTop.std,
      sm: tablet.noResults.buttonMarginTop,
      md: noResults.buttonMarginTop,
    },
    minWidth: 0,
    height: {
      xs: phone.noResults.buttonHeight.std,
      sm: noResults.buttonHeight,
    },
    p: {
      xs: phone.noResults.buttonPadding.std,
      sm: tablet.noResults.buttonPadding,
      md: noResults.buttonPadding,
    },
    bgcolor: noResults.buttonBg,
    border: `1px solid ${noResults.buttonBorder}`,
    borderRadius: {
      xs: phone.noResults.buttonRadius.std,
      sm: noResults.buttonRadius,
    },
    color: noResults.buttonColor,
    fontSize: {
      xs: phone.noResults.buttonFontSize.std,
      sm: noResults.buttonFontSize,
    },
    fontWeight: noResults.buttonFontWeight,
    lineHeight: "normal",
    textTransform: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      bgcolor: noResults.buttonBg,
      borderColor: noResults.buttonHoverBorder,
      color: noResults.buttonHoverColor,
    },
    [PHONE_SM_QUERY]: {
      marginTop: phone.noResults.buttonMarginTop.sm,
      height: phone.noResults.buttonHeight.sm,
      padding: phone.noResults.buttonPadding.sm,
      borderRadius: phone.noResults.buttonRadius.sm,
      fontSize: phone.noResults.buttonFontSize.sm,
    },
  } as SxProps<Theme>,

  /** Document-tile placeholder; the real tile's box without its status tint. */
  skeletonTile: {
    width: {
      xs: phone.row.iconTileSize.std,
      sm: tablet.row.iconTileSize,
      md: row.iconTileSize,
    },
    height: {
      xs: phone.row.iconTileSize.std,
      sm: tablet.row.iconTileSize,
      md: row.iconTileSize,
    },
    borderRadius: {
      xs: phone.row.iconTileRadius,
      sm: tablet.row.iconTileRadius,
      md: row.iconTileRadius,
    },
    flexShrink: 0,
    [PHONE_SM_QUERY]: {
      width: phone.row.iconTileSize.sm,
      height: phone.row.iconTileSize.sm,
    },
  } as SxProps<Theme>,

  /** Placeholder row — mirrors the real row's geometry so nothing jumps. */
  skeletonRow: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    gap: { xs: phone.row.gap.std, sm: tablet.row.gap, md: row.gap },
    p: { xs: phone.row.padding.std, sm: tablet.row.padding, md: row.padding },
    bgcolor: { xs: list.bg, sm: "transparent" },
    border: { xs: `1px solid ${list.border}`, sm: "none" },
    borderRadius: { xs: phone.row.radius, sm: 0 },
    borderBottom: {
      xs: `1px solid ${list.border}`,
      sm: `1px solid ${row.divider}`,
    },
    [PHONE_SM_QUERY]: {
      gap: phone.row.gap.sm,
      padding: phone.row.padding.sm,
    },
  } as SxProps<Theme>,
};

export default inboxStyles;
